import React, { Component } from 'react';
import {
  inject,
  observer,
} from 'mobx-react';
import {
  Checkbox,
  Col,
  Row,
  FormControl,
  ControlLabel,
  FormGroup,
  Button,
  Radio,
  InputGroup,
} from 'react-bootstrap';
import startCase from 'lodash/startCase';
import { ToleranceMode } from '../../constants/Configuration';
import cx from 'classnames';
import {
  Formik,
  Form,
  ErrorMessage,
} from 'formik';
import LoadingButton from '../../components/LoadingButton/LoadingButton';
import logger from '../../utils/logger';
import './Settings.css';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Loader } from '../../components/PageLoading/PageLoading';
import DownloadPlantTemplateButton from './DownloadPlantTemplateButton';
import { getValidationForModel } from './validation-schema';
import InfoTooltip from '../../components/InfoTooltip';
import { isRunModeNewYork } from '../../stores/batch/run-mode';

class Settings extends Component {
  constructor(props) {
    super(props);
    this.state = {
      errors: {},
    };
  }

  componentDidMount () {
    this.mounted = true;
  }

  componentWillUnmount () {
    this.mounted = false;
  }

  onSubmit = (values, actions) => {
    logger.log('on Submit: ', values, actions);
    const { settingsStore, notifierStore } = this.props;
    const { configuration } = settingsStore;
    this.setState({ errors: {} });

    settingsStore.saveConfiguration({
      ...configuration,
      ...values,
    })
      .then(() => {
        actions.setSubmitting(false);
      })
      .catch((e) => {
        if (this.mounted) {
          this.setState({ errors: e.errors || {} });
        }
        notifierStore.showError(`${e.message}`);
        actions.setValues(values);
        actions.setSubmitting(false);
      });
  };

  handleCheckbox = (e, name, values, setFieldValue) => {
    const checked = !!e.target.checked;
    setFieldValue(name, checked);

    const fieldConfig = this.props.model[name];
    if (fieldConfig?.inverseOf && Array.isArray(fieldConfig.inverseOf) && checked) {
      // If this field is checked, and it has inverseOf fields, uncheck each of them
      fieldConfig.inverseOf.forEach(inverse => {
        setFieldValue(inverse, false);
      });
    }
  };

  renderField(name, value, config, values, setFieldValue) {
    const { type, disabled, min, max, step, options, handleChange, required } = config;
    const { t } = this.props;

    switch (type) {
      case 'number':
      case 'text':
        return (
          <FormControl
            name={name}
            type={type}
            value={value || ''}
            disabled={disabled}
            min={min}
            max={max}
            step={step}
            onChange={handleChange}
            bsSize="sm"
          />
        );
      case 'boolean':
        return (
          <Checkbox
            inline
            className="mt-checkbox mt-checkbox-outline"
            name={name}
            disabled={disabled}
            value={value}
            checked={!!value}
            onChange={e => this.handleCheckbox(e, name, values, setFieldValue, handleChange)}
          >
            <span />
          </Checkbox>
        );
      case 'radio':
        return (
          <div>
            {options.map((option, i) => (
              <Radio
                key={i}
                inline
                className="mt-radio mt-radio-outline"
                name={name}
                value={!!option.value}
                disabled={disabled}
                checked={!!option.value ? !!values[name] : !values[name]}
                onChange={e => {
                  setFieldValue(name, !!option.value ? !!e.target.checked : !e.target.checked);
                }}
              >
                <span />
                {t(option.label)}
              </Radio>
            ))}
          </div>
        );
      default:
        return (
          <FormControl
            name={name}
            componentClass="select"
            disabled={disabled}
            value={value || ''}
            onChange={e => {
              if (name === 'runMode') {
                setFieldValue('toleranceMode', isRunModeNewYork(e.target.value)
                  ? ToleranceMode.NYDOTCumulative
                  : ToleranceMode.NetWeight
                );
              }
              handleChange(e);
            }}
            bsSize="sm"
          >
            {!required && <option value="">- None -</option>}
            {options.map(option => (
              <option key={option.value || option.id} value={option.value || option.id}>
                {option.name || option.id}
              </option>
            ))}
            {!options.length && value &&
              <option value={value}>{value}</option>
            }
          </FormControl>
        );
    }
  }

  renderForm() {
    const { settingsStore, model, data, t } = this.props;
    const config = Object.keys(model).map(fieldName => {

      if (typeof model[fieldName] === 'string') {
        return {
          fieldName,
          label: startCase(fieldName),
          type: model[fieldName],
        };
      }

      return {
        fieldName,
        label: model[fieldName].label || startCase(fieldName),
        type: model[fieldName].type,
        disabled: !!model[fieldName].disabled,
        min: model[fieldName].min,
        max: model[fieldName].max,
        step: model[fieldName].step,
        defaultValue: model[fieldName].defaultValue,
        hidden: !!model[fieldName].hidden,
        required: !!model[fieldName].required,
        checked: model[fieldName].checked,
        name: model[fieldName].name,
        options: model[fieldName].options,
        unit: model[fieldName].unit,
        tooltip: model[fieldName].tooltip,
      };

    });

    let initialValues = {};

    config.forEach(v => {
      initialValues[v.fieldName] = v.type === 'boolean' ? data[v.fieldName] : data[v.fieldName] ?? v.defaultValue;
    });

    initialValues = {
      ...data,
      ...initialValues,
    };

    return (
      <Formik
        enableReinitialize={true}
        initialValues={
          {
            ...initialValues,
          }
        }
        validateOnChange={true}
        validationSchema={getValidationForModel(model)}
        onSubmit={this.onSubmit}
      >{({
          handleChange,
          values,
          setFieldValue,
          errors,
          touched,
          isSubmitting,
          resetForm,
        }) => (
          <Form className={'settings-form'}>
            <Row>
              {Object.keys(model).map(fieldName => {
                const data = config.find(v => v.fieldName === fieldName);
                if (data.hidden) return '';
                return (
                  <Col sm={3} xs={6} key={fieldName}>
                    <FormGroup className={cx({ 'has-error': (errors[fieldName] && touched[fieldName]) || this.state.errors[fieldName] })}>
                      {data.type !== 'radio' && (
                        <ControlLabel>
                          {t(data.label)}
                          {data.tooltip && <InfoTooltip title={t(data.label)} text={data.tooltip} />}
                          {' '}
                        </ControlLabel>
                      )}
                      {!data.unit
                        ? this.renderField(fieldName, values[fieldName], {
                          ...data,
                          disabled: data.disabled || isSubmitting,
                          options: data.options || settingsStore[data.type] || [],
                          handleChange: handleChange,
                        }, values, setFieldValue)
                        : (
                          <FormGroup>
                            <InputGroup>
                              {this.renderField(fieldName, values[fieldName], {
                                ...data,
                                disabled: data.disabled || isSubmitting,
                                options: data.options || settingsStore[data.type] || [],
                                handleChange: handleChange,
                              }, values, setFieldValue)}
                              <InputGroup.Addon>
                                {data.unit}
                              </InputGroup.Addon>
                            </InputGroup>
                          </FormGroup>
                        )
                      }
                      <ErrorMessage component="span" name={fieldName} className="help-block" />
                    </FormGroup>
                  </Col>
                );
              })}
            </Row>
            <Row>
              <Col sm={6}>
                <DownloadPlantTemplateButton />
              </Col>
              <Col sm={6} className="text-right">
                <Button onClick={() => resetForm({
                  values: data,
                })}>
                  {t('Reset')}
                </Button>
                {' '}
                <LoadingButton
                  bsStyle="primary"
                  type="submit"
                  label={t('Save')}
                  loadingLabel="Saving"
                  isLoading={isSubmitting}
                />
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    );
  }

  render() {
    return !this.props.ticketStore.templates.length
      ? <Loader/>
      : (
        this.renderForm()
      );
  }
}

export default withTranslation()(inject('settingsStore', 'notifierStore', 'ticketStore')(observer(Settings)));

Settings.propTypes = {
  settingsStore: PropTypes.object.isRequired,
  ticketStore: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  model: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
};
